
import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { CompanyInfo } from '@/contexts/auth/types';

export interface Address {
  street?: string;
  postalCode?: string;
  city?: string;
  countryCode?: string;
}

export interface CompanyProfile {
  name: string;
  taxNumber?: string;
  registrationNumber?: string;
  address?: Address;
  description?: string;
  logoUrl?: string;
}

export const useCompanyProfile = () => {
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [profileId, setProfileId] = useState<string | null>(null);
  const { user, updateProfile } = useAuth();

  useEffect(() => {
    if (user && user.userProfile) {
      // Store the profile ID for later use
      if (user.userProfile['@id']) {
        setProfileId(user.userProfile['@id']);
        console.log('Profile ID stored:', user.userProfile['@id']);
      } else {
        console.warn('User profile exists but @id is missing:', user.userProfile);
      }

      // Map from userProfile.companyInfo to our CompanyProfile format
      if (user.userProfile.companyInfo) {
        const companyInfo = user.userProfile.companyInfo;
        console.log('Company info from user profile:', companyInfo);
        
        // Ensure we properly map the address object
        setCompanyProfile({
          name: companyInfo.name || '',
          taxNumber: companyInfo.taxNumber,
          registrationNumber: companyInfo.registrationNumber,
          address: {
            street: companyInfo.address?.street || '',
            postalCode: companyInfo.address?.postalCode || '',
            city: companyInfo.address?.city || '',
            countryCode: companyInfo.address?.countryCode || 'HU'
          },
          description: companyInfo.description,
          logoUrl: companyInfo.logo
        });
      } else {
        setCompanyProfile(null);
      }
    } else {
      setCompanyProfile(null);
      setProfileId(null);
    }
  }, [user]);

  const updateCompanyProfile = async (updatedProfile: CompanyProfile): Promise<CompanyProfile | null> => {
    if (!user) return null;

    setIsLoading(true);
    try {
      // Check if we have a stored profileId or can get it from the user object
      const userProfileId = profileId || (user.userProfile && user.userProfile['@id']);

      // If we still don't have a profile ID, log error and notify user
      if (!userProfileId) {
        console.error('Missing user profile ID. Profile ID:', profileId, 'User Profile:', user.userProfile);
        toast({
          title: "Hiba",
          description: "A felhasználói profil azonosítója hiányzik.",
          variant: "destructive",
        });
        setIsLoading(false);
        return null;
      }

      console.log('Using profile ID for update:', userProfileId);
      console.log('Updating company profile with data:', updatedProfile);

      // Convert our CompanyProfile format to the API's CompanyInfo format
      // Explicitly handle the address object to ensure it's properly structured
      const companyInfo: CompanyInfo = {
        name: updatedProfile.name,
        taxNumber: updatedProfile.taxNumber,
        registrationNumber: updatedProfile.registrationNumber,
        address: {
          street: updatedProfile.address?.street,
          postalCode: updatedProfile.address?.postalCode,
          city: updatedProfile.address?.city,
          countryCode: updatedProfile.address?.countryCode || 'HU'
        },
        description: updatedProfile.description,
        logo: updatedProfile.logoUrl
      };

      console.log('Converted company info for API:', companyInfo);

      // Update through the profile update mechanism
      const updateData = {
        owner: 'me',
        name: user.userProfile?.name || { firstName: '', lastName: '' },
        email: user.email || '',
        companyInfo: companyInfo
      };

      const success = await updateProfile(updateData);

      if (success) {
        // After successful update, refresh company profile from user object
        if (user.userProfile?.companyInfo) {
          setCompanyProfile({
            name: user.userProfile.companyInfo.name || '',
            taxNumber: user.userProfile.companyInfo.taxNumber,
            registrationNumber: user.userProfile.companyInfo.registrationNumber,
            address: {
              street: user.userProfile.companyInfo.address?.street || '',
              postalCode: user.userProfile.companyInfo.address?.postalCode || '',
              city: user.userProfile.companyInfo.address?.city || '',
              countryCode: user.userProfile.companyInfo.address?.countryCode || 'HU'
            },
            description: user.userProfile.companyInfo.description,
            logoUrl: user.userProfile.companyInfo.logo
          });
        }
        toast({
          title: 'Sikeres mentés',
          description: 'A cégprofil adatok sikeresen frissítve.',
        });
        return updatedProfile;
      }
      return null;
    } catch (error) {
      console.error('Failed to update company profile:', error);
      toast({
        title: 'Hiba',
        description: 'Nem sikerült frissíteni a cégprofilt.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    companyProfile,
    isLoading,
    updateCompanyProfile,
    profileId
  };
};
